/**
 * Cores das telas v2 (tokens de components/v2/tokens.css, escopo `.rv`).
 *
 * Mapa do protótipo → token (para portar o markup do mockup):
 *   #0C0E12 bg · #12151A surface · #101318 modal · #0F1216 inset · #0B0D10 deep
 *   #181C22 popover · #171B21 chip · #161A20 hover · #151920 rowhover · #1A1E24 track
 *   #15181D empty · #1F242B border · #1C2026 border-soft · #262B33 border-2
 *   #2A2F37 border-3 · #3A404A line · #E9EBEE text · #C9CDD4 text-2 · #9AA0AA text-3
 *   #8A909B muted · #6B717C faint · #5E646E dim · #4A505A dis · #AEB3BB neutral
 *   oklch good/bad/amber/violet/pink/cyan/teal/blue → c('good') etc.
 *
 * `drawer` e `shell` são tons novos (WhatsApp v2); fora de `.rv-wpp` caem em `modal` e `bg`.
 * `teal` e `blue` são matizes novos (tags e seletores de data do WhatsApp v2).
 * O WhatsApp v2 (`.rv.rv-wpp`) também define `--rv-wa-*` (cores da prévia do WhatsApp): use `wa()`.
 */
export type Tone = 'bg' | 'surface' | 'modal' | 'inset' | 'deep' | 'popover' | 'chip' | 'hover' | 'rowhover'
    | 'track' | 'empty' | 'border' | 'border-soft' | 'border-2' | 'border-3' | 'line' | 'text' | 'text-2'
    | 'text-3' | 'muted' | 'faint' | 'dim' | 'dis' | 'neutral' | 'drawer' | 'shell';
export type Hue = 'good' | 'bad' | 'amber' | 'violet' | 'pink' | 'cyan' | 'teal' | 'blue';
/** Cores da prévia do WhatsApp (só dentro de `.rv-wpp`). */
export type WaTone = 'bg' | 'in' | 'out' | 'btn' | 'link' | 'meta' | 'text' | 'card' | 'sec' | 'dot' | 'note' | 'ph';

export const HUES: readonly Hue[] = ['good', 'bad', 'amber', 'violet', 'pink', 'cyan', 'teal', 'blue'];

/** Neutro do tema: v('surface'), v('text', .5). */
export const v = (tone: Tone, alpha?: number) =>
    alpha == null ? `hsl(var(--rv-${tone}))` : `hsl(var(--rv-${tone}) / ${alpha})`;

/** Cor de status/origem: c('good'), c('bad', .12). */
export const c = (hue: Hue, alpha?: number) =>
    alpha == null ? `rgb(var(--rv-${hue}))` : `rgb(var(--rv-${hue}) / ${alpha})`;

/** Cor da prévia do WhatsApp: wa('in'), wa('link', .5). */
export const wa = (tone: WaTone, alpha?: number) =>
    alpha == null ? `rgb(var(--rv-wa-${tone}))` : `rgb(var(--rv-wa-${tone}) / ${alpha})`;

export const K = {
    good: c('good'), goodF: c('good', 0.12), bad: c('bad'), badF: c('bad', 0.12),
    amber: c('amber'), pink: c('pink'), violet: c('violet'), cyan: c('cyan'),
    neu: v('muted'), neuF: v('muted', 0.1), neutral: v('neutral'),
    /** barra abaixo da média no sentido "bom" (protótipo: oklch(0.55 0.08 290)) */
    violetDim: c('violet', 0.45),
};

/** Botão/segmento ativo (protótipo: fundo #E9EBEE, texto #0C0E12). */
export const seg = (on: boolean) =>
    on ? { background: v('text'), color: v('bg') } : { background: 'transparent', color: v('text-3') };

/** Fundo do avatar por página (protótipo: oklch(0.4 0.08 hue)). */
export const avatarBg = (hue: number) => `hsl(${hue} var(--rv-avatar-s) var(--rv-avatar-l))`;
export const avatarFg = v('text');

/** Hue estável a partir do id da página. */
export const hueOf = (id: string) => {
    const HUES_DEG = [20, 150, 285, 80, 220, 330, 50, 190, 260];
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return HUES_DEG[h % HUES_DEG.length];
};
